import { useEffect, useRef } from "react";

/**
 * Tiny pub/sub layer that lets any screen tell the Spotlight Tutorial what the user just did,
 * without the screen having to know whether a tutorial is running.
 *
 *  - Events:  one-off things that happened ("profile.saved").
 *  - Signals: the latest snapshot of a screen's state, used to gate steps ("is the address filled in?").
 */

const eventListeners = new Set();
const signalListeners = new Set();
const signalSnapshots = {};

export const emitSpotlightEvent = (name, payload = {}) => {
	eventListeners.forEach((listener) => listener({ name, payload }));
};

export const subscribeToSpotlightEvents = (listener) => {
	eventListeners.add(listener);
	return () => eventListeners.delete(listener);
};

const isShallowEqual = (a = {}, b = {}) => {
	const keys = Object.keys(a);
	return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key]);
};

const publishSignals = (scope, snapshot) => {
	if (snapshot && isShallowEqual(signalSnapshots[scope], snapshot)) return;
	if (snapshot) signalSnapshots[scope] = snapshot;
	else delete signalSnapshots[scope];
	signalListeners.forEach((listener) => listener(scope));
};

export const getSpotlightSignals = (scope) => signalSnapshots[scope];

export const subscribeToSpotlightSignals = (listener) => {
	signalListeners.add(listener);
	return () => signalListeners.delete(listener);
};

/** Publishes `snapshot` under `scope` while the calling screen is mounted. */
export const useSpotlightSignals = (scope, snapshot) => {
	useEffect(() => {
		publishSignals(scope, snapshot);
	});

	useEffect(() => () => publishSignals(scope, null), [scope]);
};

/** Emits `eventName` each time `isOpen` turns true (not for something that was already open on mount). */
export const useEmitSpotlightEventWhenOpened = (isOpen, eventName) => {
	const wasOpenRef = useRef(isOpen);
	useEffect(() => {
		if (isOpen && !wasOpenRef.current) emitSpotlightEvent(eventName);
		wasOpenRef.current = isOpen;
	}, [isOpen, eventName]);
};
